import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { fireFireworks, playCelebration } from "@/lib/confetti";
import { Certificate, X, Trophy } from "@phosphor-icons/react";

// Full-screen fanfare shown the moment the LAST remaining lesson of a course is
// completed (any order) — fireworks, a big congratulations, and an obvious
// route to view/print the certificate.
export default function CourseCompleteCelebration({ course, soundOn = true, onClose, grade = null }) {
  const nav = useNavigate();
  const timers = useRef([]);

  useEffect(() => {
    if (soundOn) playCelebration();
    fireFireworks();
    for (let i = 1; i < 5; i++) timers.current.push(setTimeout(fireFireworks, i * 1300));
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[80] flex items-center justify-center p-5"
      style={{ background: "rgba(10,13,20,0.82)", backdropFilter: "blur(10px)" }}
      data-testid="course-complete-overlay"
    >
      <motion.div
        initial={{ scale: 0.7, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.15 }}
        className="relative w-full max-w-lg rounded-3xl bg-white text-center px-8 py-10 shadow-2xl"
      >
        <button data-testid="course-complete-close" onClick={onClose} title="Keep browsing the course"
          className="absolute top-4 right-4 text-[#000000] hover:text-[#000000] bg-transparent border-0 cursor-pointer">
          <X size={22} weight="bold" />
        </button>

        <motion.div
          animate={{ rotate: [0, -8, 8, -8, 8, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 1.6, delay: 0.4 }}
          className="w-24 h-24 mx-auto rounded-full grid place-items-center mb-4"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", boxShadow: "0 12px 40px rgba(255,158,0,0.5)" }}
        >
          <Trophy size={52} weight="fill" color="#3d2c00" />
        </motion.div>

        <div className="text-xs uppercase tracking-[0.3em] text-[#8fb300] font-bold mb-1">Course complete · 100%</div>
        <h2 className="text-4xl font-extrabold font-display mb-2" data-testid="course-complete-heading">
          🎆 Congratulations! 🎆
        </h2>
        <p className="text-[#000000] mb-1">
          You finished every lesson in
        </p>
        <p className="text-xl font-bold font-display mb-6">{course.title}</p>

        <button
          data-testid="course-complete-view-certificate"
          className="btn-primary w-full text-lg flex items-center justify-center gap-2 py-3"
          onClick={() => nav(`/certificate/${course.id}${grade ? `?grade=${grade}` : ""}`)}
        >
          <Certificate size={22} weight="fill" /> View & print your certificate
        </button>
        <button
          data-testid="course-complete-keep-browsing"
          className="btn-ghost mt-3 text-sm text-[#000000] underline"
          onClick={onClose}
        >
          Keep browsing the course
        </button>
      </motion.div>
    </motion.div>
  );
}
